import { useCallback, useMemo, useRef, useState } from 'react';
import { useChatActions, useChatState } from 'flare-chat-core/react';
import { createHostActions } from './actions';
import { createHostViewModel } from '../../projection/workspace.js';
import { useProjectSidebarNavigation } from '../projects/useProjectSidebarNavigation.js';
import { useHostProjects } from '../projects/useProjects.js';
import { useWorkspaceLocationRestore } from '../projects/workspace-location.js';
import type { HostOptions } from '../../host/contracts';
import type { HostChatView } from '../context';
import { useSkills } from '../capabilities/useSkills';
import { useModeManagement } from '../capabilities/useModeManagement.js';
import { useSkillManagement } from '../capabilities/useSkillManagement.js';
import { useProjectionInvalidation, type ProjectionRead } from './useProjectionInvalidation';

export type ChatViewInput = Pick<HostChatView, 'workspaceLocation' | 'apiReadinessGate'> & {
  options: HostOptions; api: HostChatView['realApi'];
};
/** Public React snapshots are projected, never copied into a second Chat store. */
export const useChatView = ({ options, api, workspaceLocation, apiReadinessGate }: ChatViewInput): HostChatView => {
  const { skillAPI } = api;
  const state = useChatState();
  const actions = useChatActions();
  const [draftScopeKey, setDraftScopeKey] = useState(0);
  const onSessionReset = useCallback(() => setDraftScopeKey(value => value + 1), []);
  const stateRef = useRef(state);
  stateRef.current = state;
  const settlementKey = JSON.stringify(state.timeline.filter((item) => item.type === 'message')
    .map((item) => [item.message_id, item.client_request_id]));
  const skills = useSkills({ skillAPI, instanceId: options.instanceId, userId: options.userId,
    enabled: !apiReadinessGate.blocked, settlementKey });
  // Settings and action admission share the existing authoritative read owner/cache.
  const managementSkillAPI = useMemo(() => skillAPI && ({
    getSkills: () => skills.owner.readFresh(),
    updateSkill: skillAPI.updateSkill && (async (...args: Parameters<NonNullable<typeof skillAPI.updateSkill>>) => {
      await skillAPI.updateSkill?.(...args);
      return skills.owner.readFresh();
    }),
  }), [skillAPI, skills.owner]);
  const modeManagement = useModeManagement({ modeAPI: api.modeAPI, instanceId: options.instanceId });
  const skillManagement = useSkillManagement({ skillAPI: managementSkillAPI, instanceId: options.instanceId });
  const mapped = useMemo(() => createHostActions(actions, () => stateRef.current, {
    defaultSessionTitle: options.defaultSessionTitle, userId: options.userId, skills: skills.owner, onSessionReset,
  }), [actions, onSessionReset, options.defaultSessionTitle, options.userId, skills.owner]);
  // Saved delivery and exact-version tab changes invalidate totals; they never
  // supply the count. The project reader reconciles through its existing owner.
  const publicationKey = JSON.stringify([state.session.sessionId,
    state.projections.delivered_artifacts, state.session.detail?.context?.artifact_tab_projection]);
  // The pinned Core runtime exposes this read receipt; its packaged state declaration omits it.
  const sessionRead = (state.session as typeof state.session & { read: ProjectionRead }).read;
  const onProjectionInvalidated = useProjectionInvalidation({
    scopeKey: JSON.stringify([workspaceLocation.scopeKey, workspaceLocation.effectiveProjectId]),
    activeSessionId: state.session.sessionId, read: sessionRead, readSession: actions.loadSession,
  });
  const projects = useHostProjects({ api, options, actions, workspaceLocation, publicationKey,
    onProjectionInvalidated,
    ready: state.lifecycle.status === 'ready', enabled: !apiReadinessGate.blocked });
  const projectProjection = useMemo(() => ({ ...projects, projection: {
    scope: { projectId: workspaceLocation.effectiveProjectId, activeSessionId: state.session.sessionId },
    sessions: state.sessions, bootStatus: state.lifecycle.status,
  } }), [projects, state.lifecycle.status, state.session.sessionId, state.sessions, workspaceLocation.effectiveProjectId]);
  const sidebarNavigation = useProjectSidebarNavigation({ api, options, projects: projectProjection,
    workspaceLocation, onSelectSession: mapped.sessionList.onSelectSession, actions });
  const capabilityScopeKey = JSON.stringify([options.userId, options.instanceId, workspaceLocation.effectiveProjectId, state.session.sessionId || { draft: draftScopeKey, conversationDraftKey: sidebarNavigation.conversationDraft.key }]);
  const viewModel = useMemo(() => createHostViewModel(state, mapped, { ...projectProjection, sidebarNavigation }, options.contextUsageFormatters, skills, capabilityScopeKey),
    [capabilityScopeKey, mapped, options.contextUsageFormatters, projectProjection, sidebarNavigation, state, skills]);
  useWorkspaceLocationRestore({ sessionList: viewModel.sessionList, workspaceLocation });
  return useMemo(() => ({ workspaceLocation, viewModel, realApi: api, modeManagement, skillManagement,
    apiReadinessGate, blockedApiError: apiReadinessGate.error }),
  [workspaceLocation, viewModel, api, apiReadinessGate, modeManagement, skillManagement]);
};
